import { z } from "zod";
/**
 * General-purpose Zod primitives that several schemas need, kept apart from
 * any one feature's module so that e.g. a venue schema hardening a URL field
 * isn't reaching into an About-page file to do it.
 *
 * Pure and dependency-free apart from zod, so the API and the browser derive
 * identically and the rules are unit-tested where this repo keeps its pure
 * logic — the same reason `brandRamp.ts` lives here.
 */
/**
 * Zod's `.url()` is `new URL()` in a try/catch, and `new URL()` parses
 * `javascript:`, `data:`, and `vbscript:` perfectly happily. Any field using
 * this schema is expected to render as an `href` somewhere a visitor can
 * click, so the scheme is constrained here rather than trusted.
 */
export const httpUrlSchema = z
    .string()
    .trim()
    .url()
    .max(2000)
    .refine((v) => /^https?:\/\//i.test(v), {
    message: "Use a link starting with http:// or https://",
});
/**
 * An admin who clears a field sends `""`, which every validator below would
 * reject as malformed rather than read as "not set". Seven optional contact
 * fields is seven chances to forget a `|| null` at the call site, so the
 * coercion lives here where it cannot be forgotten.
 *
 * `inner` must itself accept `null` — put `.nullable()` inside the call,
 * e.g. `blankToNull(z.string().email().nullable())`. `blankToNull(schema).nullable()`
 * wraps in the wrong order: the preprocess runs first, hands `null` to
 * `schema`, and a non-nullable `schema` throws `"Expected string, received
 * null"` instead of reporting "not set". Worse, `blankToNull(z.string().email())`
 * with no `.nullable()` anywhere typechecks — the output type is inferred as
 * `string` — and still returns `null` at runtime, so the mistake is invisible
 * until something downstream dereferences the value.
 */
export const blankToNull = (inner) => z.preprocess((v) => (typeof v === "string" && v.trim() === "" ? null : v), inner);
//# sourceMappingURL=validation.js.map